import { Button, ICON_COLOR } from '@zep-us/zus';
import { ChangeIcon, SchoolIcon } from '@zep-us/zus-icons';
import { useTranslation } from 'next-i18next';
import { useValidateSchoolAndSubjectInfo } from '../../action/useValidateSchoolAndSubjectInfo';
import { useBuildInfoContext } from '../../context';
import { useSyncQuizTitleWithCategory } from '../../features/build-quiz-title';
import { useGetCategoryFormValue } from '../../features/category-form';
import { CategoryChips, useCategoryLabels } from '../../features/category-reader';
import { categoryInfoModal } from '../CategoryInfoModal';
import { ViewportResponsiveButton } from './ViewportResponsiveButton';

/**
 * 교육 정보 표시 컴포넌트
 * - 학교 정보 Chip (학교급, 학년, 학기)
 * - 과목 Chip
 * - 출판사 Chip (있는 경우)
 * - 변경 버튼 (EducationInfoModal 열기)
 */
export const CategoryInfoDisplay = () => {
  const buildInfoContext = useBuildInfoContext();
  const validate = useValidateSchoolAndSubjectInfo();
  const {
    success
  } = validate();
  const {
    t
  } = useTranslation(['build', 'common']);

  // 카테고리 정보 form hook 사용
  const {
    getCategoryFormValue,
    updateCategoryFormValue
  } = useGetCategoryFormValue();
  const categoryInfoValues = getCategoryFormValue(buildInfoContext.schoolInfoTree);

  // category-reader를 사용하여 label 생성
  const labels = useCategoryLabels({
    categoryValues: categoryInfoValues,
    schoolInfoTree: buildInfoContext.schoolInfoTree,
    subjectTree: buildInfoContext.subjectTree,
    publishers: buildInfoContext.publishers?.[buildInfoContext.userCountryCode],
    userCountryCode: buildInfoContext.userCountryCode
  });
  const syncQuizTitleWithCategory = useSyncQuizTitleWithCategory({
    schoolInfoTree: buildInfoContext.schoolInfoTree,
    subjectTree: buildInfoContext.subjectTree,
    publishers: buildInfoContext.publishers,
    userCountryCode: buildInfoContext.userCountryCode
  });
  const handleChangeClick = async () => {
    // CategoryInfoModal 열기
    // schoolInfoTree를 전달하여 현재 국가에 맞지 않는 데이터 필터링
    await categoryInfoModal.openAsync({
      formValues: categoryInfoValues,
      schoolInfoTree: buildInfoContext.schoolInfoTree,
      subjectTree: buildInfoContext.subjectTree,
      publishers: buildInfoContext.publishers,
      userCountryCode: buildInfoContext.userCountryCode,
      onComplete: values => {
        // 카테고리 정보 완료 콜백 (자동 타이틀 생성 포함)
        updateCategoryFormValue(values);
        syncQuizTitleWithCategory(values);
      }
    });
  };
  return <div className="flex cursor-pointer items-center gap-sm rounded-sm py-xs hover:bg-gray-50" onClick={handleChangeClick} data-sentry-component="CategoryInfoDisplay" data-sentry-source-file="CategoryInfoDisplay.tsx">
      <SchoolIcon size="sm" fill={ICON_COLOR.neutral} data-sentry-element="SchoolIcon" data-sentry-source-file="CategoryInfoDisplay.tsx" />

      {/* 교육 정보가 입력된 경우 */}
      {success ? <>
          <div className="flex flex-1">
            <CategoryChips labels={labels} schoolTypeId={categoryInfoValues.schoolTypeId} schoolInfoTree={buildInfoContext.schoolInfoTree} />
          </div>
          <ViewportResponsiveButton className="gap-2xs text-body-sm-semibold" leftSlot={<ChangeIcon size="xs" fill={ICON_COLOR.neutral} />}>
            {t('common:global.change.button')}
          </ViewportResponsiveButton>
        </> : <Button variant="outlined" intent="neutral" size="sm">
          <span className="text-body-md-medium text-destructive">* </span>
          {t('build:build.input.schoolLevelInfo')}
        </Button>}
    </div>;
};